import React from "react";
import { Box, Chip, Grid2, Paper, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import {
  DailyTasksDetailResult,
  DailyTasksSlaStatus,
  PROJECT_WORK_STATE_LABELS,
  ProjectWorkState,
  VARIANTS_COVERAGE_LABELS,
  variantsCoverage,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";
import { dailyTasksChipSx, getDailyTasksChipColors } from "./dailyTasksChips";
import { DailyTasksHint, DAILY_TASKS_HINTS } from "./DailyTasksHint";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";

interface ProjectDrawerBodyProps {
  detail: DailyTasksDetailResult;
}

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const projectWorkLabel = (
  state: DailyTasksDetailResult["mockup_work_state"],
): string => {
  if (state && state in PROJECT_WORK_STATE_LABELS) {
    return PROJECT_WORK_STATE_LABELS[state as ProjectWorkState];
  }
  return state ? state.replace(/_/g, " ") : "Commercially ready";
};

const slaTone = (
  theme: Theme,
  status: DailyTasksSlaStatus,
): { bg: string; fg: string; label: string } => {
  try {
    if (status === "bad") {
      return { ...getDailyTasksChipColors(theme, "bad"), label: "Overdue" };
    }
    if (status === "warn") {
      return { ...getDailyTasksChipColors(theme, "warn"), label: "Watch" };
    }
    if (status === "ok") {
      return { ...getDailyTasksChipColors(theme, "ok"), label: "On track" };
    }
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  } catch {
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  }
};

const StatTile: React.FC<{
  eyebrow: string;
  value: string;
  hint?: string;
  info?: string;
  pill?: { label: string; bg: string; fg: string };
}> = ({ eyebrow, value, hint, info, pill }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          sx={{
            color: colors.greyTextColor,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </CustomTypography>
        {info ? <DailyTasksHint text={info} /> : null}
      </Box>
      <CustomTypography
        variant="heading5"
        fontWeight={600}
        sx={{
          color: colors.navyText,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        {value}
      </CustomTypography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 0.75,
        }}
      >
        {pill ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: pill.bg,
              color: pill.fg,
            }}
          >
            <CustomTypography variant="caption" fontWeight={700} color={pill.fg}>
              {pill.label}
            </CustomTypography>
          </Box>
        ) : null}
        {hint ? (
          <CustomTypography variant="caption" sx={{ color: colors.greyTextColor }}>
            {hint}
          </CustomTypography>
        ) : null}
      </Box>
    </Box>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <CustomTypography
      variant="caption"
      fontWeight={600}
      sx={{
        color: getModernPageColors(theme).subtitle,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        display: "block",
        mb: 1.25,
      }}
    >
      {children}
    </CustomTypography>
  );
};

const MetaField: React.FC<{
  label: string;
  value: string;
  hint?: string;
}> = ({ label, value, hint }) => {
  const theme = useTheme();
  const pageColors = getModernPageColors(theme);
  return (
    <Grid2 size={{ xs: 6, sm: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minHeight: 20 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          sx={{
            color: pageColors.subtitle,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </CustomTypography>
        {hint ? <DailyTasksHint text={hint} /> : null}
      </Box>
      <CustomTypography
        variant="body2"
        fontWeight={500}
        sx={{ color: pageColors.title, mt: 0.25 }}
      >
        {value}
      </CustomTypography>
    </Grid2>
  );
};

const ProjectDrawerBody: React.FC<ProjectDrawerBodyProps> = ({ detail }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { canViewDispatchDate } = useDailyTasksFieldPermissions();
  const sla = slaTone(theme, detail.sla_status);
  const coverage = variantsCoverage(detail.variants_qty, detail.ordered_quantity);
  const finance = detail.project_finance_status;
  const financeLabel =
    finance === "approved"
      ? "Approved"
      : finance === "rejected"
        ? "Rejected"
        : finance === "pending"
          ? "Pending"
          : "No request";
  const financeTone =
    finance === "approved"
      ? "ok"
      : finance === "rejected"
        ? "bad"
        : finance === "pending"
          ? "warn"
          : "neutral";
  const workTone =
    detail.mockup_work_state === "needs_request"
      ? "bad"
      : detail.mockup_work_state === "awaiting_finance"
        ? "warn"
        : detail.mockup_work_state === "variants_incomplete"
          ? "info"
          : "ok";

  return (
    <>
      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Elapsed"
              value={formatHours(detail.sla_elapsed_hours)}
              hint={`Since ${formatDate(detail.sla_clock_start)}`}
              info={DAILY_TASKS_HINTS.drawerProjectElapsed}
              pill={{ label: sla.label, bg: sla.bg, fg: sla.fg }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Open-work SLA"
              value={formatHours(detail.sla_target_hours)}
              hint={
                detail.days_to_sla == null
                  ? undefined
                  : detail.days_to_sla < 0
                    ? `${Math.abs(detail.days_to_sla)}d past`
                    : `${detail.days_to_sla}d left`
              }
              info={DAILY_TASKS_HINTS.drawerProjectSla}
            />
          </Grid2>
        </Grid2>
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            size="small"
            label={projectWorkLabel(detail.mockup_work_state)}
            sx={dailyTasksChipSx(theme, workTone)}
          />
          <Chip
            size="small"
            label={`Finance · ${financeLabel}`}
            sx={dailyTasksChipSx(theme, financeTone)}
          />
          <Chip
            size="small"
            label={`Variants · ${VARIANTS_COVERAGE_LABELS[coverage]}`}
            sx={dailyTasksChipSx(
              theme,
              coverage === "full" ? "ok" : coverage === "partial" ? "warn" : "bad",
            )}
          />
          <Chip
            size="small"
            label={
              detail.customer_invoice_present
                ? `Invoiced · ${formatDate(detail.customer_invoice_date)}`
                : "Not invoiced"
            }
            sx={dailyTasksChipSx(
              theme,
              detail.customer_invoice_present ? "ok" : "neutral",
            )}
          />
        </Box>
        <Box sx={{ mt: 1.75 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CustomTypography
                variant="caption"
                fontWeight={600}
                sx={{ color: colors.greyTextColor }}
              >
                Variants qty
              </CustomTypography>
              <DailyTasksHint text={DAILY_TASKS_HINTS.drawerProjectVariants} />
            </Box>
            <CustomTypography
              variant="caption"
              fontWeight={600}
              sx={{ color: colors.navyText }}
            >
              {detail.variants_qty ?? 0} / {detail.ordered_quantity}
            </CustomTypography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 99,
              bgcolor: colors.veryLightGray,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${
                  detail.ordered_quantity > 0
                    ? Math.min(
                        100,
                        Math.round(
                          ((detail.variants_qty ?? 0) / detail.ordered_quantity) *
                            100,
                        ),
                      )
                    : 0
                }%`,
                height: "100%",
                bgcolor:
                  coverage === "full" ? colors.successGreen : colors.darkBlue,
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...modernSectionPaperSx(theme), mt: 2 }}>
        <SectionLabel>Order</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField label="Order" value={String(detail.order_id)} />
          <MetaField label="Product" value={detail.product_name ?? "-"} />
          <MetaField label="Brand" value={detail.brand_name ?? "-"} />
          <MetaField label="Type" value={detail.order_type ?? "-"} />
          <MetaField label="Approved" value={formatDate(detail.start_date)} />
          {canViewDispatchDate ? (
            <MetaField
              label="Dispatch"
              value={formatDate(detail.dispatch_date)}
            />
          ) : null}
          <MetaField label="PM" value={detail.pm_poc ?? "-"} />
          <MetaField label="Sales" value={detail.sales_poc ?? "-"} />
          <MetaField
            label="Finance approved"
            value={formatDate(detail.project_finance_approval_date)}
            hint={DAILY_TASKS_HINTS.drawerProjectFinance}
          />
          <MetaField
            label="Invoice date"
            value={formatDate(detail.customer_invoice_date)}
            hint={DAILY_TASKS_HINTS.drawerProjectInvoice}
          />
        </Grid2>
      </Paper>
    </>
  );
};

export default ProjectDrawerBody;
